import { asDate } from "@domain/model/Time";
import {
  asJobId,
  asClientId,
  asConstructionSiteId,
  asEventId,
  asPersonId,
  asAttachmentId,
} from "@domain/model/Ids";

// --- DTO Imports ---
import type {
  JobReportResponse,
  JobReportContextDto,
  JobReportEventDto,
  JobReportEventPayloadDto,
  JobReportCrewMemberDto,
  JobReportAttachmentDto,
  ObsSignatureDataDto,
  JobCloseDayDataDto,
  JobCompleteDataDto,
} from "../dto/JobReportDto";

// --- Domain Imports ---
import type {
  JobReport,
  JobReportContext,
  JobReportEvent,
  JobReportEventPayload,
  JobReportCrewMember,
  JobReportAttachment,
  ObsSignatureData,
  JobCloseDayData,
  JobCompleteData,
} from "@domain/model/JobReport";

export const jobReportFromDto = (d: JobReportResponse): JobReport => ({
  context: jobReportContextFromDto(d.context),
  timeline: d.timeline.map(jobReportEventFromDto),
  time: { ...d.time },
  crew: d.crew.map(jobReportCrewMemberFromDto),
  travelEstimate: { ...d.travelEstimate }, // Direct copy as there are no dates/IDs
});

export const jobReportContextFromDto = (
  d: JobReportContextDto,
): JobReportContext => ({
  jobId: asJobId(d.jobId),
  title: d.title,
  customer: {
    customerId: asClientId(d.customer.customerId),
    name: d.customer.name,
    address: d.customer.address ? { ...d.customer.address } : null,
  },
  site: {
    siteId: asConstructionSiteId(d.site.siteId),
    label: d.site.label,
    address: d.site.address ? { ...d.site.address } : null,
  },
  period: {
    start: asDate(d.period.start),
    end: asDate(d.period.end),
  },
  status: d.status,
});

export const jobReportEventFromDto = (
  d: JobReportEventDto,
): JobReportEvent => ({
  eventId: asEventId(d.eventId),
  occurredAt: asDate(d.occurredAt),
  type: d.type,
  actor: {
    personId: asPersonId(d.actor.personId),
    displayName: d.actor.displayName,
  },
  data: d.data ? eventPayloadFromDto(d.data) : null,
  attachments: d.attachments ? d.attachments.map(attachmentFromDto) : [],
});

const eventPayloadFromDto = (
  d: JobReportEventPayloadDto,
): JobReportEventPayload => ({
  jobStart: d.jobStart ? { ...d.jobStart } : null,
  jobPause: d.jobPause ? { ...d.jobPause } : null,
  jobResume: d.jobResume ? { ...d.jobResume } : null,
  jobSuspend: d.jobSuspend ? { ...d.jobSuspend } : null,
  jobCloseDay: d.jobCloseDay ? jobCloseDayFromDto(d.jobCloseDay) : null,
  jobComplete: d.jobComplete ? jobCompleteFromDto(d.jobComplete) : null,
  crewJoin: d.crewJoin
    ? { ...d.crewJoin, personId: asPersonId(d.crewJoin.personId) }
    : null,
  crewLeave: d.crewLeave
    ? { ...d.crewLeave, personId: asPersonId(d.crewLeave.personId) }
    : null,
  obsPhoto: d.obsPhoto ? { ...d.obsPhoto } : null,
  obsNote: d.obsNote ? { ...d.obsNote } : null,
  obsSignature: d.obsSignature ? obsSignatureFromDto(d.obsSignature) : null,
});

const attachmentFromDto = (d: JobReportAttachmentDto): JobReportAttachment => ({
  ...d,
  attachmentId: asAttachmentId(d.attachmentId),
});

export const jobReportCrewMemberFromDto = (
  d: JobReportCrewMemberDto,
): JobReportCrewMember => ({
  ...d,
  personId: asPersonId(d.personId),
});

const jobCloseDayFromDto = (d: JobCloseDayDataDto): JobCloseDayData => ({
  effectiveEnd: asDate(d.effectiveEnd),
  customerAbsent: d.customerAbsent,
});

const jobCompleteFromDto = (d: JobCompleteDataDto): JobCompleteData => ({
  effectiveEnd: asDate(d.effectiveEnd),
  customerAbsent: d.customerAbsent,
  note: d.note,
});

const obsSignatureFromDto = (d: ObsSignatureDataDto): ObsSignatureData => ({
  ...d,
  workDate: asDate(d.workDate),
  cutoff: asDate(d.cutoff),
});
